import React from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import ColumnMenu from "./ColumnMenu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shadcn-in-nx/ui/tooltip";

function getConstraints(column) {
    const list = [];
    if (column.type && typeof column.type === 'string') list.push(`Type: ${column.type}`);
    if (column.required) list.push("Required");
    if (Array.isArray(column.enum) && column.enum.length > 0) {
        const preview = column.enum.slice(0, 5).join(", ");
        list.push(`Enum: ${preview}${column.enum.length > 5 ? ` (+${column.enum.length - 5})` : ""}`);
    }
    if (column.const !== undefined) list.push(`Const: ${JSON.stringify(column.const)}`);
    if (column.minLength !== undefined) list.push(`Min length: ${column.minLength}`);
    if (column.maxLength !== undefined) list.push(`Max length: ${column.maxLength}`);
    if (column.minimum !== undefined) list.push(`≥ ${column.minimum}`);
    if (column.maximum !== undefined) list.push(`≤ ${column.maximum}`);
    if (column.exclusiveMinimum !== undefined) list.push(`> ${column.exclusiveMinimum}`);
    if (column.exclusiveMaximum !== undefined) list.push(`< ${column.exclusiveMaximum}`);
    if (column.multipleOf !== undefined) list.push(`Multiple of: ${column.multipleOf}`);

    if (column.pattern) {
        if (column.examples && column.examples.length > 0) {
            list.push(`Example: ${column.examples[0]}`);
        } else {
            list.push(`Pattern: ${column.patternError || column.pattern}`);
        }
    }

    if (column.format) list.push(`Format: ${column.format}`);
    if (column.default !== undefined) list.push(`Default: ${JSON.stringify(column.default)}`);
    if (column.nullable) list.push("Nullable");
    return list;
}

function ColumnHeader({
    column, sortConfig, width,
    onSort, onHide, onFilter, onDelete,
}) {
    const isSorted = sortConfig?.column === column.name;
    const direction = isSorted ? sortConfig.direction : null;

    const SortIcon = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ArrowUpDown;
    const constraints = getConstraints(column);

    return (
        <div
            className="flex items-center justify-between p-2 group"
            style={{ width: width ? `${width}px` : undefined }}
        >
            <TooltipProvider>
                <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                        <button
                            onClick={() => onSort(column.name)}
                            className="flex items-center gap-1 hover:text-primary"
                        >
                            <span className="font-bold">{column.name}</span>
                            <SortIcon className={`w-3 h-3 ${isSorted ? "opacity-100" : "opacity-0 group-hover:opacity-50"}`} />
                        </button>
                    </TooltipTrigger>
                    {(constraints.length > 0 || column.description) && (
                        <TooltipContent side="top" className="text-xs flex flex-col gap-1 max-w-[250px] p-3">
                            {column.description && (
                                <div className="mb-1.5 pb-1.5 border-b border-border/50 text-muted-foreground">
                                    {column.description}
                                </div>
                            )}
                            
                            {constraints.length > 0 && (
                                <div className="flex flex-col gap-1.5">
                                    <span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">
                                        Validations
                                    </span>
                                    {constraints.map((c, idx) => (
                                        <span key={idx} className="text-muted-foreground">• {c}</span>
                                    ))}
                                </div>
                            )}
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>
            <ColumnMenu
                columnName={column.name}
                onHide={onHide}
                onFilter={onFilter}
                onDelete={onDelete}
            />
        </div>
    );
}

export default React.memo(ColumnHeader);
